import {
  CallState,
  mapExternalResource,
  unpackExternalResource,
} from "@b2bportal/core-types";
import { GroundSelectionEnum } from "@b2bportal/ground-api";
import { ensureExhaustive } from "@overrides/utilities";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import type {
  CarsState,
  VehicleAvailabilityWithSupplier,
} from "../../../../store";
import { CarListSortComparatorsMap } from "../../../../utilities/availability";
import { initialFiltersState } from "../initialState";
import {
  performBagsFilter,
  performCancellationPolicyFilter,
  performCarCompaniesFilter,
  performCarTypeFilter,
  performPassengersFilter,
  performPriceRangeFilter,
  performSpecificationsFilter,
} from "~core-cars/utilities";

export const isSearchStateInitialized = (state: CarsState) =>
  state.cars.carsAvailability.initialized === CallState.Success;

export const getSearchFormPickupLocation = (state: CarsState) =>
  state.cars.carsAvailability.searchFormValues.pickupLocation;

export const getSearchFormPickUpLocationTrackingProperties = (
  state: CarsState
) =>
  state.cars.carsAvailability.searchFormValues.pickupLocation
    ?.trackingPropertiesV2;

export const getSearchFormPickupDate = createSelector(
  (state: CarsState) => state.cars.carsAvailability.searchFormValues.pickupDate,
  (pickupDate) => toDateIfDefined(pickupDate)
);
export const getSearchFormPickupTime = createSelector(
  (state: CarsState) => state.cars.carsAvailability.searchFormValues.pickupTime,
  (pickupTime) => toDateIfDefined(pickupTime)
);
export const getSearchFormDropoffType = (state: CarsState) =>
  state.cars.carsAvailability.searchFormValues.dropoffType;

export const getSearchFormDropoffLocation = (state: CarsState) =>
  state.cars.carsAvailability.searchFormValues.dropoffLocation;

export const getSearchFormDropOffLocationTrackingProperties = (
  state: CarsState
) =>
  state.cars.carsAvailability.searchFormValues.dropoffLocation
    ?.trackingPropertiesV2;

export const getSearchFormDropoffDate = createSelector(
  (state: CarsState) =>
    state.cars.carsAvailability.searchFormValues.dropoffDate,
  (dropoffDate) => toDateIfDefined(dropoffDate)
);
export const getSearchFormDropoffTime = createSelector(
  (state: CarsState) =>
    state.cars.carsAvailability.searchFormValues.dropoffTime,
  (dropoffTime) => toDateIfDefined(dropoffTime)
);

export const getSearchFormDriverAge = (state: CarsState) =>
  state.cars.carsAvailability.searchFormValues.driverAge;

export const getSearch = (state: CarsState) =>
  state.cars.carsAvailability.search;

export const getRequestedSearch = (state: CarsState) =>
  state.cars.carsAvailability.requestedSearch;

export const getOpaqueShopRequestContext = (state: CarsState) =>
  state.cars.carsAvailability.searchResult.data?.context
    .opaqueShopRequestContext;

export const getCarsEqualityCheck = (
  prevCars: VehicleAvailabilityWithSupplier[],
  newCars: VehicleAvailabilityWithSupplier[]
) => {
  return (
    prevCars.length === newCars.length &&
    // This should only execute if the length of the arrays is the same
    prevCars.every((car, index) => car.vehicle.id === newCars[index].vehicle.id)
  );
};

export const isLoading = (state: CarsState) => {
  const { state: callState, data } = state.cars.carsAvailability.searchResult;
  return (
    callState === CallState.InProcess && (!data || data?.vehicles.length === 0)
  );
};
export const isInitializingValues = (state: CarsState) =>
  state.cars.carsAvailability.initialized === CallState.InProcess;
export const hasError = (state: CarsState) =>
  state.cars.carsAvailability.searchResult.state === CallState.Failed;
export const hasNeverFetchedCars = (state: CarsState) =>
  state.cars.carsAvailability.searchResult.state === CallState.NotCalled;

export const isAvailabilityPageLoading = (state: CarsState) =>
  isLoading(state) || hasNeverFetchedCars(state);

export const getSort = (state: CarsState) => state.cars.carsAvailability.sort;

export const getSuppliers = (state: CarsState) =>
  state.cars.carsAvailability.searchResult.data?.context.suppliers;

export const getVehicleAvailability = (state: CarsState, vehicleId: string) =>
  state.cars.carsAvailability.searchResult.data?.vehicles.find(
    (car) => car.vehicle.id === vehicleId
  );

export const getVehicleAvailabilityWithSupplier = createSelector(
  getSuppliers,
  getVehicleAvailability,
  (_: CarsState, vehicleId: string) => vehicleId,
  (suppliers, vehicle, vehicleId): VehicleAvailabilityWithSupplier => {
    if (vehicle == null) {
      throw new Error("missing vehicle: " + vehicleId);
    }
    const supplier = suppliers?.[vehicle?.supplierRef];
    if (supplier == null) {
      // TODO: log error to datadog
      throw new Error("missing supplier: " + vehicle.supplierRef);
    }

    return {
      ...vehicle,
      supplier,
    };
  }
);

export const getAvailabilityResponse = createSelector(
  (state: CarsState) => {
    return state.cars.carsAvailability.searchResult;
  },
  (searchResult) => ({ ...searchResult })
);

export const getCars = createSelector(
  getAvailabilityResponse,
  getSort,
  (response, sort) => {
    return mapExternalResource(response, (data) => {
      const cars = data?.vehicles;
      const sortComparator = CarListSortComparatorsMap[sort];
      if (sortComparator == null) {
        return cars;
      }

      return cars?.slice().sort(sortComparator);
    });
  }
);

export const getPriceFilter = (state: CarsState) =>
  state.cars.carsAvailability.filters.priceRange;

export const getPriceFilterOptions = createSelector(getCars, (cars) => {
  const carsPrice =
    cars.data?.map((car) => car.totalCharge?.fiat).filter(Boolean) ?? [];

  if (carsPrice.length === 0) return null;

  const initialPriceOption = carsPrice[carsPrice.length - 1];

  return carsPrice.reduce(
    (acc, item) => ({
      min: item.value < acc.min.value ? item : acc.min,
      max: item.value > acc.max.value ? item : acc.max,
    }),
    { min: initialPriceOption, max: initialPriceOption }
  );
});

export const getSuppliersArray = createSelector(
  getAvailabilityResponse,
  (response) => {
    const suppliers = Object.values(response.data?.context.suppliers ?? {});
    return suppliers.sort((supplierA, supplierB) =>
      supplierA.name.localeCompare(supplierB.name)
    );
  }
);

export const getFilterOptions = createSelector(
  getPriceFilterOptions,
  getSuppliersArray,
  (priceRange, suppliers) => {
    return {
      priceRange,
      suppliers,
    };
  }
);

export const getFilters = (state: CarsState) => {
  return state.cars.carsAvailability.filters;
};

export const hasSetPriceFilterSelector = createSelector(
  getPriceFilter,
  getFilterOptions,
  (priceFilter, initialFilterOptions) => {
    return (
      (priceFilter?.max !== initialFilterOptions.priceRange?.max.value &&
        priceFilter?.max !== initialFiltersState.priceRange.max) ||
      (priceFilter?.min !== initialFilterOptions.priceRange?.min.value &&
        priceFilter.min !== initialFiltersState.priceRange.min)
    );
  }
);

export const hasSpecificationFilter = createSelector(getFilters, (filters) => {
  return Object.values(filters.specification).some(Boolean);
});

export const hasMinimumBagsFilter = createSelector(getFilters, (filters) => {
  return filters.minimumBags !== initialFiltersState.minimumBags;
});

export const hasFilters = createSelector(
  hasSetPriceFilterSelector,
  hasSpecificationFilter,
  hasMinimumBagsFilter,
  getFilters,
  (hasPriceFilter, hasSpecificationFilter, hasMinimumBagsFilter, filters) => {
    return (
      hasPriceFilter ||
      hasMinimumBagsFilter ||
      hasSpecificationFilter ||
      filters.freeCancellation ||
      filters.carType.length > 0 ||
      filters.passenger.length > 0 ||
      filters.supplier.length > 0
    );
  }
);

export const getAppliedFilters = createSelector(
  hasSetPriceFilterSelector,
  hasSpecificationFilter,
  hasMinimumBagsFilter,
  getFilters,
  (hasPriceFilter, hasSpecificationFilter, hasMinimumBagsFilter, filters) => {
    const appliedFilters: string[] = [];
    if (hasPriceFilter) appliedFilters.push("price");
    if (hasMinimumBagsFilter) appliedFilters.push("bags");
    if (hasSpecificationFilter) appliedFilters.push("specification");
    if (filters.freeCancellation) appliedFilters.push("free_cancellation");
    if (filters.carType.length > 0) appliedFilters.push("car_type");
    if (filters.passenger.length > 0) appliedFilters.push("num_passengers");
    if (filters.supplier.length > 0) appliedFilters.push("supplier");
    return appliedFilters;
  }
);

export const getFilteredCars = createSelector(
  getCars,
  getFilters,
  getSuppliers,
  (cars, filters, suppliers) => {
    return unpackExternalResource(
      mapExternalResource(cars, (data) => {
        if (data == null || suppliers == null) return undefined;
        const filteredCars: VehicleAvailabilityWithSupplier[] = (data ?? [])
          .filter((car) => {
            const filtersArray = [
              performCarTypeFilter(car, filters.carType),
              performPriceRangeFilter(car, filters.priceRange),
              performCarCompaniesFilter(car, filters.supplier),
              performSpecificationsFilter(car, filters.specification),
              performBagsFilter(car, filters.minimumBags),
              performPassengersFilter(car, filters.passenger),
              performCancellationPolicyFilter(car, filters.freeCancellation),
            ];

            return filtersArray.every((filterCondition) => filterCondition);
          })
          .map((car) => {
            return {
              ...car,
              supplier: suppliers[car.supplierRef],
            };
          });
        return filteredCars;
      })
    );
  }
);

export const getCentroid = (state: CarsState) =>
  state.cars.carsAvailability.map.centroid;

export const getMapZoom = (state: CarsState) =>
  state.cars.carsAvailability.map.zoom;

export const getMapBoundingBox = (state: CarsState) =>
  state.cars.carsAvailability.map.boundingBox;

export const getCurrentAvailabilityRequest = createSelector(
  getSearch,
  (search) => {
    if (search == null) return undefined;

    switch (search.searchType) {
      case GroundSelectionEnum.Place: {
        return {
          GroundSelection: search.searchType,
          request: search,
        };
      }
      case GroundSelectionEnum.Location:
        return undefined;
      default:
        ensureExhaustive(search.searchType, undefined);
    }
  }
);

const toDateIfDefined = (date: string | undefined) =>
  date == null ? undefined : new Date(date);

// mirrored from https://github.com/hopper-org/Daytona/blob/12c7c254234bcf143e203d2a7374092892c5f865/daytona-core/src/main/scala/com/hopper/daytona/util/OthersUtil.scala#L46-L54
export const getSearchDayCount = createSelector(getSearch, (search) => {
  if (search == null) return undefined;

  const lengthInHours = dayjs(search.dropoffTime).diff(
    dayjs(search.pickupTime),
    "hour"
  );

  const lengthInDays = Math.ceil(lengthInHours / 24);

  return lengthInDays <= 0 ? 1 : lengthInDays;
});

export const getTotalFilterCount = createSelector(
  hasSetPriceFilterSelector,
  hasSpecificationFilter,
  hasMinimumBagsFilter,
  getFilters,
  (hasPriceFilter, hasSpecificationFilter, hasMinimumBagsFilter, filters) => {
    const activeFilters = [
      hasPriceFilter,
      hasMinimumBagsFilter,
      hasSpecificationFilter,
      filters.freeCancellation,
      filters.carType.length > 0,
      filters.passenger.length > 0,
      filters.supplier.length > 0,
    ];

    return activeFilters.filter(Boolean).length;
  }
);
